import { getHttpClient } from '@/services/httpClientProvider';
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';
import type { Product, ProductListResponse, PartnerResponse } from '@/types/partner';

export interface PartnerTeam {
    id: string;
    partners_id: string;
    name: string;
    description: string;
    access_type: number;
    email: string;
    phone: string;
    avatar: string;
    nickname: string;
    time_zone: string | null;
    disable_emails: boolean;
    status: number;
    c__user_count: number;
    c__location_count: number;
    c__issue_count: number;
    c__network_count: number;
    c__d_access_type: string;
    c__d_status: string;
    url_avatar: string;
    linked_partner_team_ids?: string[];
    project_partner_team_ids?: string[];
    m__availability_periods?: any;
    availability_periods_text?: string;
    m__out_of_office_return?: string | null;
    m__out_of_office_summary?: string;
    support_did?: string;
    support_qid?: string | string[];
    phone_cancel_abandoned?: boolean;
    transcribe_audio?: boolean;
    record_rtc?: number;
    _canWrite: boolean;
    _uiAccess?: {
        edit: boolean;
        delete: boolean;
        clone: boolean;
        merge: boolean;
    };
    _highlighted?: boolean;
}

export interface TeamsListResponse {
    success: boolean;
    partners_teams: {
        totalCount: number;
        results: PartnerTeam[];
    };
    current_server_time: string;
}

export interface PartnerUser {
    id: string;
    object: string;
    object_id: string;
    role_id: string;
    email: string;
    first_name: string;
    last_name: string;
    nickname: string;
    role: number;
    status: number;
    time_zone: string;
    avatar: string;
    login_count: number;
    updated: string;
    created: string;
    last_login: string;
    full_name: string;
    public_name: string;
    c__presence: string;
    c__d_status: string;
    c__d_present: string;
    c__d_type: string;
    c__num_open_tickets: number;
    c__tech_rating_count: string;
    c__tech_rating: string;
    c__tech_pm_rating: string;
    c__tech_nps: string;
    c__role_label: string;
    c__d_role: string;
    c__team_names: string[];
    _teams_ids?: string[];
    url_avatar: string;
    country: string;
    phone_number: string;
    phone_ext: string;
    sms_number: string;
    password_expires: string;
    password_set: string;
    password_lock_token: string;
    password_policy?: {
        expiration: string;
        idle_logout: string;
        idle_timeout: number;
        lockout: string;
        pattern: string;
        pattern_regex: string;
        rotation: string;
        session_terminate: string;
    };
    _canWrite: boolean;
    _uiAccess: {
        edit: boolean;
        delete: boolean;
        clone: boolean;
        merge: boolean;
    };
    _highlighted?: boolean;
}

export interface UsersListResponse {
    success: boolean;
    users: {
        totalCount: number;
        results: PartnerUser[];
    };
    current_server_time: string;
}

export interface PartnerToken {
    id: string;
    partners_id: string;
    creator_user_id: string | null;
    token: string;
    content: string;
    data: any | null;
    status: number;
    updated: string;
    created: string;
    metadata: any | null;
    _title: string;
    c__owner_partner_name: string;
    c__owner_partner_nickname: string;
    c__d_status: string;
    c__creator_user_name: string | null;
    c__creator_nickname: string;
    c__creator_user_id: string | null;
    c__data: string;
    _highlighted: boolean;
    _highlightmap: Record<string, any>;
    _canWrite: boolean;
    _uiAccess?: {
        edit: boolean;
        delete: boolean;
        clone: boolean;
        merge: boolean;
    };
    _has_detail: boolean;
}

export interface TokensListResponse {
    success: boolean;
    partners_tokens: {
        totalCount: number;
        results: PartnerToken[];
    };
    current_server_time: string;
}

export interface PartnerTeamIntegration {
    id: string;
    partners_teams_id: string;
    partners_id: string;
    partner_scope: boolean;
    name: string;
    type: number;
    description: string;
    status: number;
    testmode: boolean;
    created: string;
    updated: string;
    health_status: number;
    health_last_success_time: string | null;
    health_last_notify_time: string | null;
    c__d_status: string;
    c__d_type: string;
    c__partners_id: string;
    c__partners_teams_nickname: string;
    c__partners_teams_name: string;
    c__partners_teams_tz: string;
    c__banner_ids: any[];
    c__namespace: string;
    avatar: string;
    _canWrite: boolean;
    _uiAccess: {
        edit: boolean;
        delete: boolean;
        clone: boolean;
        merge: boolean;
    };
    preauth_url: string | null;
    configured: boolean;
    www_url: string;
    c__wizard: boolean;
    _has_detail: boolean;
    [key: string]: any; // For additional metadata fields
}

export interface TeamIntegrationsListResponse {
    success: boolean;
    partners_teams_integrations: {
        totalCount: number;
        results: PartnerTeamIntegration[];
    };
    current_server_time: string;
}

export interface ApiAccessKeys {
    service: string;
    token: string;
    object: string;
    objectId: string;
    privateKey: string;
}

export interface RegenerateApiTokenResponse {
    success: boolean;
    apiAccessKeys: ApiAccessKeys;
    current_server_time: string;
}

/**
 * Composable that provides access to Partner API functions.
 * This safely initializes the httpClient with authStore only when called 
 * inside components or other composables after Pinia is ready.
 */
export function usePartnerAPI() {
    const authStore = useAuthStore();
    const httpClient = getHttpClient(authStore);

    return {
        async fetchProducts(): Promise<ProductListResponse> {
            try {
                const response = await httpClient.get('admin/v4/partners/', {
                    sAction: 'listingTemplate',
                    page: '1',
                    start: '0',
                    limit: '1000'
                });
                return response as ProductListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error fetching products:', error);
                throw error;
            }
        },

        async fetchPartnerTeams(ecosystem: boolean = false): Promise<any> {
            try {
                const response = await httpClient.get('admin/v4/core/', {
                    sAction: 'metaPartnersTeams'
                });
                return response;
            } catch (error) {
                console.error('❌ PartnerAPI: Error fetching partner teams:', error);
                throw error;
            }
        },

        async fetchCurrentPartner(partnerId: string): Promise<PartnerResponse> {
            try {
                const response = await httpClient.get('admin/v4/partners/', {
                    sAction: 'listing',
                    context_org_id: '',
                    id: partnerId
                });
                return response as PartnerResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error fetching current partner:', error);
                throw error;
            }
        },

        async fetchTeamsList(params?: {
            page?: number;
            start?: number;
            limit?: number;
            query?: string;
            sortField?: string;
            sortOrder?: number;
        }): Promise<TeamsListResponse> {
            try {
                const userStore = useUserStore();
                const partnersId = userStore.userData?.context_org_id || userStore.userData?.object_id;

                const queryParams: Record<string, string> = {
                    sAction: 'teamsListing',
                    page: String(params?.page ?? 1),
                    start: String(params?.start ?? 0),
                    limit: String(params?.limit ?? 25),
                    filter: JSON.stringify([
                        { property: 'id', value: '_no_filter_' },
                        { property: 'partners_id', value: partnersId || '_no_filter_' },
                        { property: 'users_id', value: '_no_filter_' },
                        { property: 'access_type', value: '_no_filter_' },
                        { property: 'dict_id', value: '_no_filter_' }
                    ])
                };

                // Add query parameter if search term is provided
                if (params?.query) {
                    queryParams.query = params.query;
                    queryParams.fields = JSON.stringify([]);
                }

                // Add sort parameter if provided
                if (params?.sortField && params?.sortOrder) {
                    queryParams.sort = JSON.stringify([{
                        property: params.sortField,
                        direction: params.sortOrder === 1 ? 'ASC' : 'DESC'
                    }]);
                } else {
                    // Default sort by name ascending
                    queryParams.sort = JSON.stringify([{
                        property: 'name',
                        direction: 'ASC'
                    }]);
                }

                const response = await httpClient.get('admin/v4/partners/', queryParams);
                return response as TeamsListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error fetching teams list:', error);
                throw error;
            }
        },

        async fetchTeamById(teamId: string): Promise<TeamsListResponse> {
            try {
                const userStore = useUserStore();
                const partnersId = userStore.userData?.context_org_id || userStore.userData?.object_id;

                const response = await httpClient.get('admin/v4/partners/', {
                    sAction: 'teamsListing',
                    page: '1',
                    start: '0',
                    limit: '1',
                    filter: JSON.stringify([
                        { property: 'id', value: teamId },
                        { property: 'partners_id', value: partnersId || '_no_filter_' },
                        { property: 'users_id', value: '_no_filter_' },
                        { property: 'access_type', value: '_no_filter_' },
                        { property: 'dict_id', value: '_no_filter_' }
                    ])
                });
                return response as TeamsListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error fetching team by ID:', error);
                throw error;
            }
        },

        async fetchUsersList(params: {
            teamId?: string;
            page?: number;
            start?: number;
            limit?: number;
            sortField?: string;
            sortOrder?: number;
            query?: string;
        }): Promise<UsersListResponse> {
            try {
                const userStore = useUserStore();
                const partnersId = userStore.userData?.context_org_id || userStore.userData?.object_id;

                const sort = params.sortField && params.sortOrder !== undefined
                    ? JSON.stringify([{ property: params.sortField, direction: params.sortOrder === 1 ? 'ASC' : 'DESC' }])
                    : JSON.stringify([{ property: 'email', direction: 'ASC' }]); // Default sort

                const queryParams: Record<string, string> = {
                    sAction: 'listing',
                    page: String(params.page ?? 1),
                    start: String(params.start ?? 0),
                    limit: String(params.limit ?? 25),
                    sort: sort,
                    filter: JSON.stringify([
                        { property: 'id', value: '_no_filter_' },
                        { property: 'object', value: 'partners' },
                        { property: 'object_id', value: partnersId || '_no_filter_' },
                        { property: 'project_partner_users', value: params.teamId || '_no_filter_' }, // Filter by team ID if provided
                        { property: 'role_id', value: '_no_filter_' }
                    ])
                };

                // Add query parameter if search term is provided
                if (params.query) {
                    queryParams.query = params.query;
                    queryParams.fields = JSON.stringify([]);
                }

                const response = await httpClient.get('admin/v4/users/', queryParams);
                return response as UsersListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error fetching users list:', error);
                throw error;
            }
        },

        async fetchUserById(userId: string): Promise<UsersListResponse> {
            try {
                const userStore = useUserStore();
                const partnersId = userStore.userData?.context_org_id || userStore.userData?.object_id;

                const response = await httpClient.get('admin/v4/users/', {
                    sAction: 'listing',
                    page: '1',
                    start: '0',
                    limit: '25',
                    sort: JSON.stringify([{ property: 'email', direction: 'ASC' }]),
                    filter: JSON.stringify([
                        { property: 'id', value: userId }, // Filter by specific user ID
                        { property: 'object', value: 'partners' },
                        { property: 'object_id', value: partnersId || '_no_filter_' },
                        { property: 'project_partner_users', value: '_no_filter_' },
                        { property: 'role_id', value: '_no_filter_' }
                    ])
                });
                return response as UsersListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error fetching user by ID:', error);
                throw error;
            }
        },

        async fetchTokensList(params?: {
            page?: number;
            start?: number;
            limit?: number;
            query?: string;
        }): Promise<TokensListResponse> {
            try {
                const userStore = useUserStore();
                const partnersId = userStore.userData?.context_org_id || userStore.userData?.object_id;

                const queryParams: Record<string, string> = {
                    sAction: 'tokensListing',
                    page: String(params?.page ?? 1),
                    start: String(params?.start ?? 0),
                    limit: String(params?.limit ?? 25),
                    filter: JSON.stringify([
                        { property: 'id', value: '_no_filter_' },
                        { property: 'partners_id', value: partnersId || '_no_filter_' }
                    ])
                };

                // Add query and fields if search query is provided
                if (params?.query) {
                    queryParams.query = params.query;
                    queryParams.fields = JSON.stringify([]);
                }

                const response = await httpClient.get('admin/v4/partners/', queryParams);
                return response as TokensListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error fetching tokens list:', error);
                throw error;
            }
        },

        async fetchTokenById(tokenId: string): Promise<TokensListResponse> {
            try {
                const userStore = useUserStore();
                const partnersId = userStore.userData?.context_org_id || userStore.userData?.object_id;

                const response = await httpClient.get('admin/v4/partners/', {
                    sAction: 'tokensListing',
                    page: '1',
                    start: '0',
                    limit: '25',
                    filter: JSON.stringify([
                        { property: 'id', value: tokenId },
                        { property: 'partners_id', value: partnersId || '_no_filter_' }
                    ])
                });
                return response as TokensListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error fetching token by ID:', error);
                throw error;
            }
        },

        async fetchTeamIntegrations(params?: {
            page?: number;
            start?: number;
            limit?: number;
            integrationTypes?: number[]; // Filter by integration types (e.g., [22] for SAML)
        }): Promise<TeamIntegrationsListResponse> {
            try {
                const userStore = useUserStore();
                const partnersId = userStore.userData?.context_org_id || userStore.userData?.object_id;

                // Build filter array
                const filters: any[] = [
                    { property: 'id', value: '_no_filter_' },
                    { property: 'partners_teams_id', value: '_no_filter_' },
                    { property: 'partners_id', value: partnersId || '_no_filter_' }
                ];

                // Add type filter if specified
                if (params?.integrationTypes && params.integrationTypes.length > 0) {
                    filters.push({
                        value: params.integrationTypes,
                        property: 'type',
                        operator: 'in'
                    });
                }

                const response = await httpClient.get('admin/v4/partners/', {
                    sAction: 'teamsIntegrationsListing',
                    page: String(params?.page ?? 1),
                    start: String(params?.start ?? 0),
                    limit: String(params?.limit ?? 25),
                    sort: JSON.stringify([{ property: 'name', direction: 'ASC' }]),
                    filter: JSON.stringify(filters)
                });
                return response as TeamIntegrationsListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error fetching team integrations:', error);
                throw error;
            }
        },

        async createToken(tokenName: string): Promise<TokensListResponse> {
            try {
                const userStore = useUserStore();
                const partnersId = userStore.userData?.context_org_id || userStore.userData?.object_id;

                const tokenData = {
                    partners_id: partnersId,
                    id: 'tokens.TokensModel-2',
                    creator_users_id: '',
                    token: tokenName,
                    content: '<bt/><span></span>',
                    status: 0,
                    updated: null,
                    created: null,
                    _merge: '',
                    merge_ids: ''
                };

                const formData = new URLSearchParams();
                formData.append('fields', JSON.stringify([]));
                formData.append('partners_tokens', JSON.stringify([tokenData]));

                // httpClient.post automatically handles URLSearchParams and sets correct content-type
                const response = await httpClient.post('admin/v4/partners/', formData, {
                    sAction: 'tokensPut'
                });
                return response as TokensListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error creating token:', error);
                throw error;
            }
        },

        async fetchTeamIntegrationById(integrationId: string): Promise<TeamIntegrationsListResponse> {
            try {
                const response = await httpClient.get('admin/v4/partners/', {
                    sAction: 'teamsIntegrationsListing',
                    id: integrationId
                });
                return response as TeamIntegrationsListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error fetching team integration by ID:', error);
                throw error;
            }
        },

        async deleteToken(tokenId: string): Promise<{ success: boolean; current_server_time: string }> {
            try {
                const formData = new URLSearchParams();
                formData.append('fields', JSON.stringify([]));
                formData.append('partners_tokens', JSON.stringify([{ id: tokenId }]));

                const response = await httpClient.post('admin/v4/partners/', formData, {
                    sAction: 'tokensDelete'
                });

                return response as { success: boolean; current_server_time: string };
            } catch (error) {
                console.error('❌ PartnerAPI: Error deleting token:', error);
                throw error;
            }
        },

        async createTeamIntegration(partnersTeamsId: string, type: number): Promise<TeamIntegrationsListResponse> {
            try {
                const response = await httpClient.post('admin/v4/partners/', {
                    sAction: 'teamIntegrationCreateForType',
                    partners_teams_id: partnersTeamsId,
                    type: type
                });
                return response as TeamIntegrationsListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error creating team integration:', error);
                throw error;
            }
        },

        async updateTeamIntegration(integrationData: any): Promise<TeamIntegrationsListResponse> {
            try {
                const response = await httpClient.post('admin/v4/partners/', {
                    sAction: 'teamIntegrationPut',
                    partners_teams_integrations: integrationData
                });
                return response as TeamIntegrationsListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error updating team integration:', error);
                throw error;
            }
        },

        async deleteTeamIntegration(integrationId: string): Promise<any> {
            try {
                const formData = new URLSearchParams();
                formData.append('partners_teams_integrations', JSON.stringify([{ id: integrationId }]));
                
                const response = await httpClient.post(
                    'admin/v4/partners/',
                    formData,
                    { sAction: 'teamIntegrationDelete' },
                    { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
                );
                return response;
            } catch (error) {
                console.error('❌ PartnerAPI: Error deleting team integration:', error);
                throw error;
            }
        },

        async updateToken(tokenId: string, tokenName: string, content: string): Promise<TokensListResponse> {
            try {
                const tokenData = {
                    id: tokenId,
                    token: tokenName,
                    content: `<bt/>${content}` // Add <bt/> prefix to content
                };

                const formData = new URLSearchParams();
                formData.append('fields', JSON.stringify([]));
                formData.append('partners_tokens', JSON.stringify([tokenData]));

                const response = await httpClient.post('admin/v4/partners/', formData, {
                    sAction: 'tokensPut'
                });

                return response as TokensListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error updating token:', error);
                throw error;
            }
        },

        async fetchPartnerDetails(partnerId: string): Promise<PartnerResponse> {
            try {
                const response = await httpClient.get('admin/v4/partners/', {
                    sAction: 'listing',
                    context_org_id: '',
                    id: partnerId
                });
                return response as PartnerResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error fetching partner details:', error);
                throw error;
            }
        },

        async updatePartner(params: {
            id: string;
            name?: string;
            name_legal?: string;
            description?: string;
            website?: string;
            project_partner_ids?: string[];
            [key: string]: any;
        }): Promise<any> {
            try {
                const userStore = useUserStore();
                const contextOrgId = userStore.userData?.context_org_id || '';
                
                // Build the partner object with only the fields that are provided
                const partnerUpdate: any = {
                    id: params.id
                };
                
                // Add fields that are provided in params
                if (params.name !== undefined) partnerUpdate.name = params.name;
                if (params.name_legal !== undefined) partnerUpdate.name_legal = params.name_legal;
                if (params.description !== undefined) partnerUpdate.description = params.description;
                if (params.website !== undefined) partnerUpdate.website = params.website;
                if (params.project_partner_ids !== undefined) partnerUpdate.project_partner_ids = params.project_partner_ids;
                if (params.default_partners_teams_id !== undefined) partnerUpdate.default_partners_teams_id = params.default_partners_teams_id;
                if (params.m__auto_send_customer_survey !== undefined) partnerUpdate.m__auto_send_customer_survey = params.m__auto_send_customer_survey;
                if (params.issue_view_override !== undefined) partnerUpdate.issue_view_override = params.issue_view_override;
                
                // Email configuration fields
                if (params.smtp_relay_host !== undefined) partnerUpdate.smtp_relay_host = params.smtp_relay_host;
                if (params.email_aliases !== undefined) partnerUpdate.email_aliases = params.email_aliases;
                if (params._teams_email_enabled !== undefined) partnerUpdate._teams_email_enabled = params._teams_email_enabled;
                
                // Subdomain field
                if (params.subdomain !== undefined) partnerUpdate.subdomain = params.subdomain;
                
                // Security fields
                if (params.password_policy !== undefined) partnerUpdate.password_policy = params.password_policy;
                if (params.strict_ip_validation !== undefined) partnerUpdate.strict_ip_validation = params.strict_ip_validation;
                if (params.c__ip_whitelist !== undefined) partnerUpdate.c__ip_whitelist = params.c__ip_whitelist;
                if (params.email_domains !== undefined) partnerUpdate.email_domains = params.email_domains;
                
                // Data redaction fields
                if (params.m__button_redact_patterns !== undefined) partnerUpdate.m__button_redact_patterns = params.m__button_redact_patterns;
                if (params.m__auto_redact_patterns !== undefined) partnerUpdate.m__auto_redact_patterns = params.m__auto_redact_patterns;
                if (params.m__realtime_auto_redact !== undefined) partnerUpdate.m__realtime_auto_redact = params.m__realtime_auto_redact;
                
                // Format the request body as URL-encoded form data
                const formData = new URLSearchParams();
                formData.append('context_org_id', contextOrgId);
                formData.append('partners', JSON.stringify([partnerUpdate]));
                
                // Use httpClient.post with proper signature: post(path, body, params, headers)
                const response = await httpClient.post(
                    'admin/v4/partners/',
                    formData,
                    { sAction: 'put' },
                    { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
                );
                
                return response;
            } catch (error) {
                console.error('❌ PartnerAPI: Error updating partner:', error);
                throw error;
            }
        },

        async createTeam(params: {
            name: string;
            email: string;
            description?: string;
        }): Promise<TeamsListResponse> {
            try {
                const userStore = useUserStore();
                const partnersId = userStore.userData?.context_org_id || userStore.userData?.object_id;

                if (!partnersId) {
                    throw new Error('No partner ID found');
                }

                // Build the team object with required fields
                const teamData: any = {
                    partners_id: partnersId,
                    name: params.name,
                    email: params.email,
                    description: params.description || '',
                    access_type: 1, // Important: Set to 1 for the right type of team
                    status: 1, // Active status
                    type: '900',
                    phone: '',
                    avatar: '',
                    nickname: '',
                    auto_assign_route: false,
                    auto_assign_sponsor: false,
                    sponsor_sub_sku: '',
                    notification_training: '10',
                    exclusive: false,
                    published: false,
                    private_label: false,
                    link_color: '',
                    text_color: '',
                    brand_color1: '',
                    brand_color2: '',
                    brand_logo_1x: '',
                    brand_logo_2x: '',
                    brand_logo_3x: '',
                    support_email: '',
                    support_phone: '',
                    support_url: '',
                    support_did: '',
                    record_rtc: 0,
                    record_rtc_interval: 2,
                    transcribe_audio: false,
                    transcribe_video: false,
                    appdirect_marketplace_id: '',
                    appdirect_marketplace_skus: '',
                    chart_paths: '',
                    use_partner_tech_network: false,
                    hourly_rounding_factor: 10,
                    boomtown_visibility: true,
                    idb_bot_diagnosis: false,
                    phone_cancel_abandoned: false,
                    email_issue_create: false,
                    email_issue_create_address: ''
                };

                // Format the request body as URL-encoded form data
                const formData = new URLSearchParams();
                formData.append('partners_teams', JSON.stringify([teamData]));

                // Use httpClient.post with proper signature: post(path, body, params, headers)
                const response = await httpClient.post(
                    'admin/v4/partners/',
                    formData,
                    { sAction: 'teamPut' },
                    { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
                );

                return response as TeamsListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error creating team:', error);
                throw error;
            }
        },

        async updateTeam(teamId: string, updates: {
            name?: string;
            description?: string;
            email?: string;
            phone?: string;
            [key: string]: any;
        }): Promise<TeamsListResponse> {
            try {
                // Build the team object with the ID and fields to update
                const teamData: any = {
                    id: teamId,
                    ...updates
                };

                // Format the request body as URL-encoded form data
                const formData = new URLSearchParams();
                formData.append('partners_teams', JSON.stringify([teamData]));

                // Use httpClient.post with proper signature: post(path, body, params, headers)
                const response = await httpClient.post(
                    'admin/v4/partners/',
                    formData,
                    { sAction: 'teamPut' },
                    { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
                );

                return response as TeamsListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error updating team:', error);
                throw error;
            }
        },

        async deleteTeam(teamId: string): Promise<{ success: boolean; current_server_time: string }> {
            try {
                // Build the team object with just the ID for deletion
                const teamData = {
                    id: teamId
                };

                // Format the request body as URL-encoded form data
                const formData = new URLSearchParams();
                formData.append('partners_teams', JSON.stringify([teamData]));

                // Use httpClient.post with proper signature: post(path, body, params, headers)
                const response = await httpClient.post(
                    'admin/v4/partners/',
                    formData,
                    { sAction: 'teamDelete' },
                    { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
                );

                return response as { success: boolean; current_server_time: string };
            } catch (error) {
                console.error('❌ PartnerAPI: Error deleting team:', error);
                throw error;
            }
        },

        /**
         * Clone an existing team
         * @param teamData The complete team data object to clone
         * @returns Promise with the cloned team response
         */
        async cloneTeam(teamData: any): Promise<TeamsListResponse> {
            try {
                // Format the request body as URL-encoded form data
                const formData = new URLSearchParams();
                formData.append('partners_teams', JSON.stringify([teamData]));

                // Use httpClient.post with proper signature: post(path, body, params, headers)
                const response = await httpClient.post(
                    'admin/v4/partners/',
                    formData,
                    { sAction: 'teamPut' },
                    { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
                );

                return response as TeamsListResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error cloning team:', error);
                throw error;
            }
        },

        /**
         * Upload partner avatar
         * @param blob The image blob to upload
         * @param partnerId The partner ID
         * @returns Promise with the upload response
         */
        async uploadPartnerAvatar(blob: Blob, partnerId: string): Promise<any> {
            try {
                // Create FormData object
                const formData = new FormData();

                // Refresh the CSRF token to ensure we have the latest one
                try {
                    await authStore.refreshCsrfToken();
                } catch (tokenError) {
                    console.warn('Failed to refresh CSRF token before upload:', tokenError);
                }

                // Add CSRF token
                formData.append('_csrf_token', httpClient.csrf);

                // Add client instance ID
                formData.append('_client_instance_id', httpClient.instanceId);

                // Add file metadata
                formData.append('object', 'partners');
                formData.append('object_id', partnerId);
                formData.append('file_tag', 'avatar');

                // Add the file
                formData.append('file', blob, 'avatar.jpg');

                // Add note field (required by API)
                formData.append('note', 'null');

                // Use httpClient for file upload
                const response = await httpClient.post<any>(
                    'admin/v4/files/',
                    formData,
                    { sAction: 'putFile' }
                );

                if (response.success) {
                    return response;
                }

                throw new Error(response.message || 'Failed to upload avatar');
            } catch (error) {
                console.error('❌ PartnerAPI: Error uploading avatar:', error);
                throw error;
            }
        },

        /**
         * Upload team avatar
         * @param blob The image blob to upload
         * @param teamId The team ID
         * @returns Promise with the upload response
         */
        async uploadTeamAvatar(blob: Blob, teamId: string): Promise<any> {
            try {
                // Create FormData object
                const formData = new FormData();

                // Refresh the CSRF token to ensure we have the latest one
                try {
                    await authStore.refreshCsrfToken();
                } catch (tokenError) {
                    console.warn('Failed to refresh CSRF token before upload:', tokenError);
                }

                // Add CSRF token
                formData.append('_csrf_token', httpClient.csrf);

                // Add client instance ID
                formData.append('_client_instance_id', httpClient.instanceId);

                // Add file metadata
                formData.append('object', 'partners_teams');
                formData.append('object_id', teamId);
                formData.append('file_tag', 'avatar');

                // Add the file
                formData.append('file', blob, 'avatar.jpg');

                // Add note field (required by API)
                formData.append('note', 'null');

                // Use httpClient for file upload
                const response = await httpClient.post<any>(
                    'admin/v4/files/',
                    formData,
                    { sAction: 'putFile' }
                );

                if (response.success) {
                    return response;
                }

                throw new Error(response.message || 'Failed to upload avatar');
            } catch (error) {
                console.error('❌ PartnerAPI: Error uploading avatar:', error);
                throw error;
            }
        },

        async regenerateApiAccessToken(partnerId: string): Promise<RegenerateApiTokenResponse> {
            try {
                const response = await httpClient.post('admin/v4/partners/', {
                    sAction: 'putApiAccessToken',
                    id: partnerId,
                    regenerate: 'true'
                });
                return response as RegenerateApiTokenResponse;
            } catch (error) {
                console.error('❌ PartnerAPI: Error regenerating API access token:', error);
                throw error;
            }
        }
    };
} 